import React, {useMemo, useState} from "react";
import moment from "moment";
import "moment-timezone";
import PropTypes from "prop-types";
import {Modal, ModalBody, ModalHeader, ModalFooter, Row, Col} from "reactstrap";
import DatePicker from "react-datepicker";
import {toast} from "react-toastify";
import ZeusButton from "../Button";
import "react-datepicker/dist/react-datepicker.css";
import "./rangePicker.scss";

/** This component allows us to select range between start time and end time */

const RangePicker = (props) => {
  const {
    open = false,
    onCloseHandler = () => {},
    handleSave = () => {},
    timeZone = "UTC",
  } = props;
  const start_date = new Date();
  start_date.setDate(start_date.getDate() - 1);
  start_date.setMinutes(0);
  start_date.setSeconds(0);
  const [startDate, setStartDate] = useState(start_date);
  const end_date = new Date();
  end_date.setMinutes(0);
  end_date.setSeconds(0);
  const [endDate, setEndDate] = useState(end_date);
  //default value is based on windowPeriod for 1 day

  const windowPeriod = useMemo(() => {
    const diffTime = Math.abs(endDate - startDate);
    let diffWithUnit;
    if (diffTime < 1000) {
      diffWithUnit = {value: diffTime, unit: "ms", aggregationUnit: "ms"};
    } else if (diffTime < 1000 * 60) {
      diffWithUnit = {
        value: Math.ceil(diffTime / 1000),
        unit: "s",
        aggregationUnit: "ms",
      };
    } else if (diffTime < 1000 * 60 * 60) {
      diffWithUnit = {
        value: Math.ceil(diffTime / (1000 * 60)),
        unit: "m",
        aggregationUnit: "s",
      };
    } else if (diffTime < 1000 * 60 * 60 * 24) {
      diffWithUnit = {
        value: Math.ceil(diffTime / (1000 * 60 * 60)),
        unit: "h",
        aggregationUnit: "m",
      };
    } else {
      diffWithUnit = {
        value: Math.ceil(diffTime / (1000 * 60 * 60 * 24)),
        unit: "d",
        aggregationUnit: "h",
      };
    }
    /** Calculating windowPeriod for backend
     * value will be divided by 2
     *  Unit will be previous unit to actual unit
     */
    return `${diffWithUnit.value / 2}${diffWithUnit.aggregationUnit}`;
  }, [endDate, startDate]);

  const setSelectedZone = (date) => {
    const dateWithoutZone = moment.tz(date, timeZone).format("YYYY-MM-DDTHH:mm:ss.SSS");
    const localZone = moment(dateWithoutZone).format("Z");
    const dateWithLocalZone = [dateWithoutZone, localZone].join("");
    return new Date(dateWithLocalZone);
  };

  const setOtherZone = (date) => {
    const dateWithoutZone = moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS");
    const otherZone = moment.tz(date, timeZone).format("Z");
    const dateWithOtherZone = [dateWithoutZone, otherZone].join("");
    return new Date(dateWithOtherZone);
  };

  const applyTimeRange = () => {
    if (startDate >= endDate) {
      toast.error("Start Time must be before End Time");
      return;
    }
    onCloseHandler();
    handleSave(startDate, endDate, windowPeriod);
  };

  return (
    <Modal unmountOnClose={true} isOpen={open} backdrop={"static"} size={"lg"}>
      <ModalHeader toggle={onCloseHandler}>
        <div>Select Time Range</div>
      </ModalHeader>
      <ModalBody>
        <div>
          <Row pl-lg-4>
            <Col xs="12" lg="6">
              <div className="date-picker-container">
                <div className="date-picker-header roboto-bold">Start Time</div>
                <div>
                  <DatePicker
                    selected={
                      timeZone === "Local" ? startDate : setSelectedZone(startDate)
                    }
                    onChange={(date) => {
                      if (timeZone === "Local") {
                        setStartDate(date);
                      } else {
                        const newDate = setOtherZone(date);
                        setStartDate(newDate);
                      }
                    }}
                    inline={true}
                    startOpen={true}
                    dateFormat="yyyy-MM-dd HH:mm"
                    showTimeSelect={true}
                    timeFormat="HH:mm"
                    shouldCloseOnSelect={false}
                    disabledKeyboardNavigation={true}
                    timeIntervals={60}
                    fixedHeight={true}
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </Col>
            <Col xs="12" lg="6">
              <div className="date-picker-container">
                <div className="date-picker-header roboto-bold">End Time</div>
                <div>
                  <DatePicker
                    selected={timeZone === "Local" ? endDate : setSelectedZone(endDate)}
                    onChange={(date) => {
                      if (timeZone === "Local") {
                        setEndDate(date);
                      } else {
                        const newDate = setOtherZone(date);
                        setEndDate(newDate);
                      }
                    }}
                    inline={true}
                    startOpen={true}
                    dateFormat="yyyy-MM-dd HH:mm"
                    showTimeSelect={true}
                    timeFormat="HH:mm"
                    shouldCloseOnSelect={false}
                    disabledKeyboardNavigation={true}
                    timeIntervals={60}
                    fixedHeight={true}
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center mt--3">
        <ZeusButton data-cy="submit-btn" onClick={applyTimeRange}>
          Apply Time Range
        </ZeusButton>
      </ModalFooter>
    </Modal>
  );
};

RangePicker.propTypes = {
  /** Component will only be visible if open is set to true */
  open: PropTypes.bool,
  onCloseHandler: PropTypes.func,
  handleSave: PropTypes.func,
  timeZone: PropTypes.string,
};

export default RangePicker;
